import React, { useMemo } from 'react';
import styled from 'styled-components';

const MatrixContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  
  @media (max-width: 768px) {
    border-radius: 6px;
  }
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: separate; /* spacing between cells */
  border-spacing: 12px 12px; /* horizontal and vertical gaps */
  min-width: 600px;
  
  @media (max-width: 768px) {
    min-width: 500px;
    border-spacing: 10px 10px;
  }
  
  @media (max-width: 480px) {
    min-width: 400px;
    border-spacing: 8px 8px;
  }
`;

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  background: #f8fafc;
  border: none;
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '13px'};
  white-space: nowrap; /* prevent wrapping when columns are resized */
  overflow: hidden;
  text-overflow: ellipsis; /* show ellipsis if header text exceeds column width */
  
  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: ${props => props.fontSize ? `${Math.max(12, props.fontSize - 1)}px` : '12px'};
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: ${props => props.fontSize ? `${Math.max(11, props.fontSize - 2)}px` : '11px'};
  }
`;

const Td = styled.td`
  padding: 0; /* spacing is handled by table border-spacing; padding inside CellBox */
  vertical-align: middle;
  border: none;
`;

const CellBox = styled.div`
  width: 100%;
  background: #eef2ff; /* light blue like screenshot */
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  padding: 6px;
  height: ${props => (props.cellHeight ? `${props.cellHeight}px` : 'auto')};
  display: flex;
  align-items: center;
`;

const CellInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 44px; /* Better touch target */
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 16px;
    min-height: 44px;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// MatrixField value shape: { [rowKey]: { [colKey]: primitive|boolean } }
// For radio rows, stores the selected column key per row; for checkbox, boolean per cell; for input, text/number per cell
const MatrixField = ({ field, value = {}, onChange }) => {
  const rows = useMemo(() => {
    if (Array.isArray(field.matrixRows) && field.matrixRows.length > 0) return field.matrixRows;
    const count = Number.isFinite(field.matrixRowsCount) ? Math.max(1, field.matrixRowsCount) : 10;
    return Array.from({ length: count }, (_, i) => `row_${i}`);
  }, [field.matrixRows, field.matrixRowsCount]);
  const cols = useMemo(() => {
    if (Array.isArray(field.matrixColumns) && field.matrixColumns.length > 0) return field.matrixColumns;
    const count = Number.isFinite(field.matrixColumnsCount) ? Math.max(1, field.matrixColumnsCount) : 5;
    return Array.from({ length: count }, (_, i) => `col_${i + 1}`);
  }, [field.matrixColumns, field.matrixColumnsCount]);
  const cellType = field.matrixCellType || 'radio'; // 'radio' | 'checkbox' | 'text' | 'number'
  const hideRowLabels = !!field.hideRowLabels;
  const cellWidth = Number.isFinite(field.matrixCellWidth) ? field.matrixCellWidth : 180;
  const cellHeight = Number.isFinite(field.matrixCellHeight) ? field.matrixCellHeight : 36;
  const rowFontSize = Number.isFinite(field.matrixRowFontSize) ? field.matrixRowFontSize : 14;
  const columnFontSize = Number.isFinite(field.matrixColumnFontSize) ? field.matrixColumnFontSize : 14;

  const getRowKey = (row) => (typeof row === 'string' ? row : row.key || row.label);
  const getColKey = (col, idx) => (typeof col === 'string' ? col : col.key || col.label || `col_${idx + 1}`);
  const getRowLabel = (row) => (typeof row === 'string' ? row : row.label || row.key);
  const getColLabel = (col, idx) => (typeof col === 'string' ? col : col.label || col.key || `Column ${idx + 1}`);

  const handleRadio = (rowKey, colKey) => {
    const next = { ...(value || {}) };
    next[rowKey] = colKey;
    onChange(next);
  };

  const handleCheckbox = (rowKey, colKey, checked) => {
    const next = { ...(value || {}) };
    const row = { ...(next[rowKey] || {}) };
    row[colKey] = !!checked;
    next[rowKey] = row;
    onChange(next);
  };

  const handleInput = (rowKey, colKey, newVal) => {
    const next = { ...(value || {}) };
    const row = { ...(next[rowKey] || {}) };
    row[colKey] = newVal;
    next[rowKey] = row;
    onChange(next);
  };

  return (
    <MatrixContainer>
      <MatrixTable>
        <thead>
          <tr>
            {!hideRowLabels && <Th fontSize={columnFontSize} style={{ width: 180 }}></Th>}
            {cols.map((c, idx) => {
              const colWidth = typeof c === 'object' && Number.isFinite(c.width) ? c.width : cellWidth;
              return (
                <Th key={idx} fontSize={columnFontSize} style={{ width: colWidth, minWidth: 0 }}>{getColLabel(c, idx)}</Th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, rIdx) => {
            const rowKey = getRowKey(r);
            const rowLabel = getRowLabel(r);
            const rowVal = value?.[rowKey];
            return (
              <tr key={rIdx}>
                {!hideRowLabels && (
                  <Td>
                    <div style={{ fontWeight: 500, color: '#374151', background: '#fcfcfd', padding: '8px 12px', borderRadius: '6px', fontSize: `${rowFontSize}px` }}>{rowLabel}</div>
                  </Td>
                )}
                {cols.map((c, cIdx) => {
                  const colKey = getColKey(c, cIdx);
                  const colHeight = typeof c === 'object' && Number.isFinite(c.height) ? c.height : cellHeight;
                  const isChecked = cellType === 'radio' ? rowVal === colKey : !!rowVal?.[colKey];
                  const inputVal = cellType === 'text' || cellType === 'number' ? rowVal?.[colKey] || '' : undefined;
                  return (
                    <Td key={cIdx}>
                      <CellBox cellHeight={colHeight} style={{ minWidth: (typeof c === 'object' && Number.isFinite(c.width) ? c.width : cellWidth) }}>
                        {cellType === 'radio' && (
                          <Center>
                            <input
                              type="radio"
                              name={`matrix-${field.id}-${rowKey}`}
                              checked={isChecked}
                              onChange={() => handleRadio(rowKey, colKey)}
                            />
                          </Center>
                        )}
                        {cellType === 'checkbox' && (
                          <Center>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleCheckbox(rowKey, colKey, e.target.checked)}
                            />
                          </Center>
                        )}
                        {(cellType === 'text' || cellType === 'number') && (
                          <CellInput
                            type={cellType === 'number' ? 'number' : 'text'}
                            value={inputVal}
                            onChange={(e) => handleInput(rowKey, colKey, e.target.value)}
                          />
                        )}
                      </CellBox>
                    </Td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </MatrixTable>
    </MatrixContainer>
  );
};

export default MatrixField;


